import { useEffect, useRef } from "react";
import { useAtomValue } from "jotai";
import { GET_USER_BALANCE } from "../apis/lambda/GraphQL/Stripe/queries";
import { userInfoAtom } from "../atoms";
import { isLoggedIn } from "../utils/jwtUtils";
import useCacheOrQuery from "./useCacheOrQuery";

const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Custom hook to fetch and cache user's Stripe balance.
 *
 * Features:
 * - Fetches balance on mount if cache is expired or missing
 * - Stores data and timestamp in localStorage to persist across page refreshes
 * - Respects 2-minute TTL (doesn't refetch unless TTL expired)
 * - Provides manual refetch function that bypasses TTL
 * - Clears cache on logout
 */
export function useUserBalance() {
  const userInfo = useAtomValue(userInfoAtom);
  const isCreator = userInfo?.userType === "CREATOR";
  const wasLoggedInRef = useRef(isLoggedIn());

  const { data, loading, error, refetch, clearCache } = useCacheOrQuery(
    GET_USER_BALANCE,
    { ttl: CACHE_TTL_MS },
    "userBalance",
    !isCreator // Skip if not a creator
  );

  // Clear cache only when user actually logs out (not on initial load)
  useEffect(() => {
    const currentlyLoggedIn = isLoggedIn();

    // If we were logged in before but now we're not, user has logged out
    if (wasLoggedInRef.current && !currentlyLoggedIn) {
      clearCache();
    }

    wasLoggedInRef.current = currentlyLoggedIn;
  }, [userInfo, clearCache]);

  return {
    balance: data?.getUserBalance ?? 0,
    loading,
    error,
    refetch,
  };
}
